import {
  ArrowLeft,
  Bell,
  Flame,
  Footprints,
  Heart,
  Moon,
  Utensils,
  Zap,
  Activity,
  Trophy,
  Apple,
  MessageCircle,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";

// â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

type MemberStatus = "good" | "warning" | "alert" | "inactive";

interface MemberData {
  name: string;
  initial: string;
  status: MemberStatus;
  relation?: string;
}

// â”€â”€ Demo data helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const getDemoData = (name: string) => ({
  biteBalanceScore: name === "Dad" ? 42 : name === "Sarah" ? 71 : name === "Mom" ? 58 : 85,
  biteBalanceLabel: name === "Dad" ? "Needs Improvement" : name === "Sarah" ? "Good" : name === "Mom" ? "Fair" : "Excellent",
  caloriesIntake: name === "Dad" ? 2540 : name === "Sarah" ? 1820 : name === "Mom" ? 1650 : 2100,
  caloriesGoal: 2000,
  caloriesBurned: name === "Dad" ? 320 : name === "Sarah" ? 480 : name === "Mom" ? 210 : 560,
  steps: name === "Dad" ? 3200 : name === "Sarah" ? 8700 : name === "Mom" ? 4100 : 9500,
  stepsGoal: 10000,
  sleepHours: name === "Dad" ? 5.5 : name === "Sarah" ? 7.2 : name === "Mom" ? 6.8 : 7.5,
  heartRate: name === "Dad" ? 88 : name === "Sarah" ? 72 : name === "Mom" ? 76 : 68,
  streak: name === "Dad" ? 2 : name === "Sarah" ? 5 : name === "Mom" ? 3 : 5,
  mealsLogged: name === "Dad" ? 1 : name === "Sarah" ? 3 : name === "Mom" ? 2 : 3,
  mealsTotal: 3,
  protein: name === "Dad" ? 65 : name === "Sarah" ? 92 : name === "Mom" ? 74 : 110,
  carbs: name === "Dad" ? 310 : name === "Sarah" ? 185 : name === "Mom" ? 198 : 230,
  fat: name === "Dad" ? 95 : name === "Sarah" ? 61 : name === "Mom" ? 56 : 72,
  fiber: name === "Dad" ? 8 : name === "Sarah" ? 22 : name === "Mom" ? 16 : 28,
  meals: name === "Dad"
    ? [{ name: "Breakfast", items: "Paratha, Chai", calories: 620, logged: true }]
    : name === "Sarah"
    ? [
        { name: "Breakfast", items: "Oatmeal, Banana", calories: 420, logged: true },
        { name: "Lunch", items: "Grilled Chicken Salad", calories: 510, logged: true },
        { name: "Dinner", items: "Brown Rice, Dal", calories: 580, logged: true },
      ]
    : name === "Mom"
    ? [
        { name: "Breakfast", items: "Idli, Sambar", calories: 380, logged: true },
        { name: "Lunch", items: "Roti, Sabzi, Curd", calories: 620, logged: true },
      ]
    : [
        { name: "Breakfast", items: "Eggs, Toast, OJ", calories: 490, logged: true },
        { name: "Lunch", items: "Wrap, Salad", calories: 620, logged: true },
        { name: "Dinner", items: "Salmon, Veggies", calories: 710, logged: true },
      ],
  weeklyTrend: name === "Dad" ? "down" : name === "Sarah" ? "up" : name === "Mom" ? "stable" : "up",
  recoveryNote:
    name === "Dad"
      ? "High calorie intake + low activity â€” recovery needed"
      : name === "Sarah"
      ? "On track! Great meals and active today"
      : name === "Mom"
      ? "Decent day, could use more steps"
      : "Excellent health day!",
});

// â”€â”€ Status config â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const statusConfig: Record<MemberStatus, { ring: string; bg: string; text: string; badge: string; label: string; headerGrad: string }> = {
  good:     { ring: "ring-2 ring-emerald-400 ring-offset-2 ring-offset-card", bg: "bg-emerald-100 dark:bg-emerald-900/50", text: "text-emerald-700 dark:text-emerald-300", badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",  label: "On Track",       headerGrad: "from-emerald-50 to-teal-50/40 dark:from-emerald-950/40 dark:to-teal-950/20 border-emerald-100/80 dark:border-emerald-900/30" },
  warning:  { ring: "ring-2 ring-amber-400 ring-offset-2 ring-offset-card",   bg: "bg-amber-100 dark:bg-amber-900/40",    text: "text-amber-700 dark:text-amber-300",   badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",       label: "Needs Attention",headerGrad: "from-amber-50 to-yellow-50/40 dark:from-amber-950/40 dark:to-yellow-950/20 border-amber-100/80 dark:border-amber-900/30" },
  alert:    { ring: "ring-2 ring-red-500 ring-offset-2 ring-offset-card",     bg: "bg-red-100 dark:bg-red-900/40",        text: "text-red-700 dark:text-red-300",       badge: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",             label: "Needs Recovery", headerGrad: "from-red-50 to-rose-50/40 dark:from-red-950/40 dark:to-rose-950/20 border-red-100/80 dark:border-red-900/30" },
  inactive: { ring: "ring-2 ring-border ring-offset-2 ring-offset-card",      bg: "bg-muted",                             text: "text-muted-foreground",               badge: "bg-muted text-muted-foreground",                                            label: "Not Active",     headerGrad: "from-muted/50 to-muted/20 border-border" },
};

// â”€â”€ Score color â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const getBBSColor = (score: number) => {
  if (score >= 80) return "text-emerald-600 dark:text-emerald-400";
  if (score >= 60) return "text-blue-600 dark:text-blue-400";
  if (score >= 40) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
};

const getBBSRingColor = (score: number) => {
  if (score >= 80) return "#10b981";
  if (score >= 60) return "#3b82f6";
  if (score >= 40) return "#f59e0b";
  return "#ef4444";
};

const getBBSBg = (score: number) => {
  if (score >= 80) return "bg-emerald-500";
  if (score >= 60) return "bg-blue-500";
  if (score >= 40) return "bg-amber-500";
  return "bg-red-500";
};

// â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const GroupMemberDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const member: MemberData = location.state?.member ?? {
    name: "Dad",
    initial: "D",
    status: "alert" as MemberStatus,
    relation: "Family",
  };

  const demo = getDemoData(member.name);
  const sc = statusConfig[member.status];
  const circumference = 2 * Math.PI * 38;
  const dashOffset = circumference - (demo.biteBalanceScore / 100) * circumference;
  const stepsPercent = Math.min((demo.steps / demo.stepsGoal) * 100, 100);
  const calPercent  = Math.min((demo.caloriesIntake / demo.caloriesGoal) * 100, 100);

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{ minHeight: "100vh", maxHeight: "100vh", overflow: "hidden" }}
    >
      {/* Blurred Background */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{ backgroundImage: `url(${welcomeBg})` }}
      />
      <div className="absolute inset-0 bg-background/85" />

      <div className="scrollbar-hide relative z-10 overflow-y-auto pb-24" style={{ height: "100vh" }}>

        {/* â”€â”€ Sticky Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="sticky top-0 z-20 flex items-center justify-between bg-background/80 px-4 py-3 backdrop-blur-md">
          <Button
            variant="ghost" size="icon"
            onClick={() => navigate(-1)}
            className="h-9 w-9 rounded-xl border border-border bg-card/80 shadow-sm"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-subhead font-bold text-foreground">Family Member</h1>
          <Button
            variant="ghost" size="icon"
            className="h-9 w-9 rounded-xl border border-border bg-card/80 shadow-sm"
          >
            <Bell className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>

        <div className="space-y-3 px-4 pt-1 pb-4">

          {/* â”€â”€ Hero Profile Card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <Card className={`overflow-hidden rounded-2xl border bg-gradient-to-br shadow-none ${sc.headerGrad}`}>
            {/* Top section */}
            <div className="flex items-center gap-4 p-5">
              {/* BBS ring avatar */}
              <div className="relative flex-shrink-0">
                <svg width="92" height="92" viewBox="0 0 92 92">
                  <circle cx="46" cy="46" r="38" fill="none" stroke="currentColor" strokeWidth="6" className="text-border/40" />
                  <circle
                    cx="46" cy="46" r="38"
                    fill="none"
                    stroke={getBBSRingColor(demo.biteBalanceScore)}
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    transform="rotate(-90 46 46)"
                  />
                </svg>
                <div className={`absolute inset-[11px] flex items-center justify-center rounded-full ${sc.bg} ${sc.ring}`}>
                  <span className={`text-title1 font-black ${sc.text}`}>{member.initial}</span>
                </div>
                {member.status === "alert" && (
                  <span className="absolute right-0.5 top-0.5 h-4 w-4 rounded-full border-2 border-card bg-red-500 shadow-sm" />
                )}
              </div>

              {/* Name / info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <h2 className="text-title2 font-black text-foreground">{member.name}</h2>
                  <span className={`rounded-md px-2 py-0.5 text-compact-xs font-bold uppercase tracking-wide ring-1 ring-inset ring-black/[0.06] dark:ring-white/[0.08] ${sc.badge}`}>
                    {sc.label}
                  </span>
                </div>
                {member.relation && (
                  <p className="mt-0.5 text-compact-sm text-muted-foreground">{member.relation}</p>
                )}
                {/* Streak */}
                <div className="mt-2 flex items-center gap-1.5">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/40">
                    <Trophy className="h-3 w-3 text-orange-500" />
                  </div>
                  <span className="text-caption2 font-bold text-foreground">{demo.streak} day streak</span>
                </div>
                {/* Recovery note */}
                <p className="mt-1.5 text-compact-sm leading-snug text-muted-foreground">{demo.recoveryNote}</p>
              </div>

              {/* BBS Score — informational */}
              <div className="flex flex-col items-center gap-1 self-start" role="presentation">
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${getBBSBg(demo.biteBalanceScore)} ring-1 ring-inset ring-black/10 dark:ring-white/10`}>
                  <span className="text-title3 font-black leading-none text-white">{demo.biteBalanceScore}</span>
                </div>
                <span className="text-2xs font-bold uppercase tracking-wider text-muted-foreground">BBS</span>
                <span className={`text-2xs font-bold ${getBBSColor(demo.biteBalanceScore)}`}>
                  {demo.biteBalanceLabel.split(" ")[0]}
                </span>
              </div>
            </div>

            {/* Send Nudge */}
            <div className="border-t border-border/60 px-5 py-3.5">
              <Button className="w-full gap-2 rounded-xl bg-violet-600 text-caption1 font-semibold text-white shadow-sm shadow-violet-200 hover:bg-violet-700 dark:shadow-violet-900/30">
                <MessageCircle className="h-4 w-4" />
                Send a Nudge
              </Button>
            </div>
          </Card>

          {/* â”€â”€ Quick Stats â€” 3 chips â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { icon: <Utensils className="h-5 w-5 text-orange-500" />,  value: `${demo.mealsLogged}/${demo.mealsTotal}`, label: "Meals",        border: "border-orange-200/60 dark:border-orange-800/30", bg: "bg-gradient-to-b from-orange-50 to-orange-100/40 dark:from-orange-950/30 dark:to-orange-900/10" },
              { icon: <Apple className="h-5 w-5 text-red-500" />,         value: demo.caloriesIntake.toLocaleString(),    label: "kcal In",      border: "border-red-200/60 dark:border-red-800/30",    bg: "bg-gradient-to-b from-red-50 to-red-100/40 dark:from-red-950/30 dark:to-red-900/10" },
              { icon: <Flame className="h-5 w-5 text-amber-500" />,       value: `${demo.caloriesBurned}`,               label: "kcal Burned",  border: "border-amber-200/60 dark:border-amber-800/30", bg: "bg-gradient-to-b from-amber-50 to-amber-100/40 dark:from-amber-950/30 dark:to-amber-900/10" },
            ].map((s) => (
              <Card key={s.label} className={`flex flex-col items-center gap-1.5 rounded-2xl border p-3.5 shadow-none ${s.border} ${s.bg}`}>
                {s.icon}
                <span className="text-callout font-black leading-none text-foreground">{s.value}</span>
                <span className="text-compact-xs font-medium text-muted-foreground text-center leading-tight">{s.label}</span>
              </Card>
            ))}
          </div>

          {/* â”€â”€ Nutrition Card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <Card className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <div className="flex items-center gap-2 border-b border-border/60 px-4 py-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/40">
                <Zap className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-caption1 font-bold text-foreground">Today&apos;s Nutrition</h3>
            </div>

            <div className="p-4 space-y-3">
              {/* Calorie bar */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-compact-sm font-semibold text-muted-foreground">Calorie Intake</span>
                  <span className="text-compact-sm font-bold text-foreground">
                    {demo.caloriesIntake} <span className="text-muted-foreground font-normal">/ {demo.caloriesGoal} kcal</span>
                  </span>
                </div>
                <div className="relative h-2.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full rounded-full ${calPercent > 110 ? "bg-red-500" : calPercent > 90 ? "bg-amber-500" : "bg-emerald-500"}`}
                    style={{ width: `${Math.min(calPercent, 100)}%` }}
                  />
                </div>
                <p className="mt-1 text-compact-xs text-muted-foreground text-right">{Math.round(calPercent)}% of goal</p>
              </div>

              {/* Macro grid */}
              <div className="grid grid-cols-4 gap-1.5">
                {[
                  { label: "Protein", value: `${demo.protein}g`, color: "text-blue-600 dark:text-blue-400",   bg: "bg-blue-50 dark:bg-blue-950/30" },
                  { label: "Carbs",   value: `${demo.carbs}g`,   color: "text-orange-600 dark:text-orange-400", bg: "bg-orange-50 dark:bg-orange-950/30" },
                  { label: "Fat",     value: `${demo.fat}g`,     color: "text-amber-600 dark:text-amber-400",  bg: "bg-amber-50 dark:bg-amber-950/30" },
                  { label: "Fiber",   value: `${demo.fiber}g`,   color: "text-green-600 dark:text-green-400",  bg: "bg-green-50 dark:bg-green-950/30" },
                ].map((mac) => (
                  <div key={mac.label} className={`flex flex-col items-center rounded-lg border border-transparent py-2.5 ring-1 ring-inset ring-black/[0.04] dark:ring-white/[0.06] ${mac.bg}`}>
                    <span className={`text-caption1 font-black ${mac.color}`}>{mac.value}</span>
                    <span className="text-2xs font-semibold text-muted-foreground mt-0.5">{mac.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* â”€â”€ Activity Card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <Card className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <div className="flex items-center gap-2 border-b border-border/60 px-4 py-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/40">
                <Activity className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-caption1 font-bold text-foreground">Activity</h3>
            </div>

            <div className="p-4 space-y-3">
              {/* Steps */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Footprints className="h-4 w-4 text-violet-500" />
                    <span className="text-caption2 font-semibold text-foreground">Steps</span>
                  </div>
                  <span className="text-caption2 font-bold text-foreground">
                    {demo.steps.toLocaleString()} <span className="text-muted-foreground font-normal text-compact-sm">/ {demo.stepsGoal.toLocaleString()}</span>
                  </span>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-violet-500" style={{ width: `${stepsPercent}%` }} />
                </div>
                <p className="mt-1 text-compact-xs text-muted-foreground text-right">{Math.round(stepsPercent)}% of goal</p>
              </div>

              {/* Sleep + HR */}
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2.5 rounded-lg border border-dashed border-indigo-200/70 bg-indigo-50/80 p-3 dark:border-indigo-700/40 dark:bg-indigo-950/25">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-900/50">
                    <Moon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-subhead font-black text-foreground leading-tight">{demo.sleepHours}h</p>
                    <p className="text-compact-xs text-muted-foreground">Sleep</p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5 rounded-lg border border-dashed border-rose-200/70 bg-rose-50/80 p-3 dark:border-rose-700/40 dark:bg-rose-950/25">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-rose-100 dark:bg-rose-900/50">
                    <Heart className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                  </div>
                  <div>
                    <p className="text-subhead font-black text-foreground leading-tight">{demo.heartRate}</p>
                    <p className="text-compact-xs text-muted-foreground">BPM</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* â”€â”€ Meals Logged â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <Card className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/40">
                  <Utensils className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                </div>
                <h3 className="text-caption1 font-bold text-foreground">Today's Meals</h3>
              </div>
              <span className="rounded-md border border-dashed border-border bg-muted/30 px-2.5 py-0.5 text-compact-xs font-semibold text-muted-foreground">
                {demo.mealsLogged}/{demo.mealsTotal} logged
              </span>
            </div>

            <div className="divide-y divide-border/40">
              {(["Breakfast", "Lunch", "Dinner"] as const).map((mealName) => {
                const logged = demo.meals.find((m) => m.name === mealName);
                return (
                  <div key={mealName} className="flex items-center gap-3 px-4 py-3">
                    <div className="shrink-0">
                      {logged
                        ? <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                        : <Circle className="h-5 w-5 text-muted-foreground/30" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-caption1 font-semibold ${logged ? "text-foreground" : "text-muted-foreground"}`}>{mealName}</p>
                      {logged ? (
                        <p className="text-compact-sm text-muted-foreground truncate">{logged.items}</p>
                      ) : (
                        <p className="text-compact-sm text-muted-foreground/60 italic">Not logged yet</p>
                      )}
                    </div>
                    {logged && (
                      <span className="shrink-0 rounded-md border border-orange-200/80 bg-orange-50/50 px-2 py-0.5 text-compact-sm font-semibold text-orange-700 dark:border-orange-800/50 dark:bg-orange-950/30 dark:text-orange-400">
                        {logged.calories} kcal
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>


        </div>
      </div>

      <BottomNavBar />
    </div>
  );
};

export default GroupMemberDetailPage;
